/**
 * Self-test suite: Username system (claims + discoverability).
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/UsernameSelfTest.swift` — same groups,
 * same case names, same assertions. Zero external network calls; all crypto
 * via SubtleCrypto in the current browser/Node runtime.
 *
 * Groups:
 *   A. Canonicalisation  — valid name, uppercase lowercased, whitespace trimmed,
 *      non-NFC rejected, Cyrillic homograph rejected, length floor/ceiling,
 *      leading digit rejected, discriminator out of range rejected.
 *   B. Formatting + parse — canonical string zero-pads; round-trip; parse full
 *      handle; parse bare name returns null; parse 1-digit discriminator null.
 *   C. Lookup hash — deterministic; differs for different names; differs for
 *      different discriminators; 0x1F unit separator prevents foo+1234 vs
 *      foo1+234 collision; output is 32 bytes.
 *   D. Claim codec — sign+verify round-trip; encode+decode round-trip; tampered
 *      claimedAt fails verify; wrong identity key throws invalidSignature;
 *      isClaim distinguishes from sibling prefixes; unsupported schema rejected.
 *   E. Discoverability — nil → not in fuzzy; directLookupOnly → not in fuzzy;
 *      discoverable → in fuzzy; defaultFor → directLookupOnly.
 *
 * Registered as "Username system (claims + discoverability)" with
 * `isVerified: false`.
 */

import {
  SelfTestRunner,
  buildSuiteResult,
  pass,
  fail,
} from "../selftest";
import type { TestCase } from "../selftest";
import {
  buildUsername,
  validateRawName,
  parseUsername,
  usernameCanonicalString,
  lookupHash,
  UsernameValidationError,
} from "./Username";
import {
  CURRENT_SCHEMA,
  CLAIM_PREFIX,
  claimCanonicalBytes,
  signClaim,
  verifyClaim,
  encodeClaim,
  decodeClaim,
  isClaim,
  UsernameClaimError,
  type UsernameClaimPayload,
} from "./UsernameClaim";
import {
  canAppearInFuzzySearch,
  defaultDiscoverabilitySettings,
} from "./UsernameDiscoverabilityPolicy";

// ─── keygen helper ────────────────────────────────────────────────────────────

/** Generate a fresh Ed25519 keypair; return raw bytes for both keys. */
async function generateEd25519KeyPair(): Promise<{
  publicKey: Uint8Array;
  privateKey: Uint8Array;
}> {
  const kp = await crypto.subtle.generateKey(
    { name: "Ed25519" },
    true,
    ["sign", "verify"],
  );
  const pubBuf = await crypto.subtle.exportKey("raw", kp.publicKey);
  // The "pkcs8" export wraps the 32-byte seed; strip the ASN.1 header (last 32 bytes).
  // Alternatively, use "raw" for the private key — but WebCrypto only allows
  // "pkcs8" for Ed25519 private key export. We extract the raw seed from the tail.
  const privPkcs8 = await crypto.subtle.exportKey("pkcs8", kp.privateKey);
  // PKCS#8 for Ed25519: the 32-byte seed is the last 32 bytes of the structure.
  const privFull = new Uint8Array(privPkcs8);
  const privateKey = privFull.slice(privFull.length - 32);
  return {
    publicKey: new Uint8Array(pubBuf),
    privateKey,
  };
}

// ─── Group A: canonicalisation ────────────────────────────────────────────────

async function testValidName(): Promise<TestCase> {
  const name = "valid name+discriminator builds Username";
  try {
    const u = buildUsername("alice", 1234);
    const ok = u.canonicalName === "alice" && u.discriminator === 1234;
    return ok ? pass(name) : fail(name, `got ${JSON.stringify(u)}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testUppercaseLowercased(): Promise<TestCase> {
  const name = "uppercase input is lowercased";
  try {
    const u = buildUsername("ALICE", 1);
    const ok = u.canonicalName === "alice";
    return ok ? pass(name) : fail(name, `got ${u.canonicalName}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testWhitespaceTrimmed(): Promise<TestCase> {
  const name = "leading/trailing whitespace is trimmed";
  try {
    const u = buildUsername("  alice  ", 1);
    const ok = u.canonicalName === "alice";
    return ok ? pass(name) : fail(name, `got ${u.canonicalName}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testNonNFCRejected(): Promise<TestCase> {
  const name = "non-NFC input is rejected";
  // "café" as NFD: c, a, f, e + U+0301 (combining acute).
  // Lowercase of that is still NFD. The NFC check must fire before the
  // charset check so the error kind is precise.
  const nfd = "café";
  try {
    validateRawName(nfd);
    return fail(name, "validateRawName did not throw");
  } catch (e) {
    if (e instanceof UsernameValidationError && e.detail.kind === "nameRequiresNFCNormalisation") {
      return pass(name);
    }
    return fail(name, `wrong error: ${String(e)}`);
  }
}

async function testCyrillicHomographRejected(): Promise<TestCase> {
  const name = "Cyrillic homograph is rejected";
  // Cyrillic small letter A (U+0430) is visually identical to Latin 'a'.
  const homograph = "аlice";
  try {
    validateRawName(homograph);
    return fail(name, "validateRawName did not throw");
  } catch (e) {
    if (e instanceof UsernameValidationError && e.detail.kind === "nameInvalidCharacter") {
      return pass(name);
    }
    return fail(name, `wrong error: ${String(e)}`);
  }
}

async function testNameTooShortRejected(): Promise<TestCase> {
  const name = "name shorter than minimum is rejected";
  try {
    validateRawName("al"); // 2 chars
    return fail(name, "validateRawName did not throw");
  } catch (e) {
    if (e instanceof UsernameValidationError && e.detail.kind === "nameTooShort" && e.detail.length === 2) {
      return pass(name);
    }
    return fail(name, `wrong error: ${String(e)}`);
  }
}

async function testNameTooLongRejected(): Promise<TestCase> {
  const name = "name longer than maximum is rejected";
  const tooLong = "a".repeat(33);
  try {
    validateRawName(tooLong);
    return fail(name, "validateRawName did not throw");
  } catch (e) {
    if (e instanceof UsernameValidationError && e.detail.kind === "nameTooLong" && e.detail.length === 33) {
      return pass(name);
    }
    return fail(name, `wrong error: ${String(e)}`);
  }
}

async function testLeadingDigitRejected(): Promise<TestCase> {
  const name = "name starting with digit is rejected";
  try {
    validateRawName("1alice");
    return fail(name, "validateRawName did not throw");
  } catch (e) {
    if (e instanceof UsernameValidationError && e.detail.kind === "ambiguousNumericPrefix") {
      return pass(name);
    }
    return fail(name, `wrong error: ${String(e)}`);
  }
}

async function testDiscriminatorOutOfRangeRejected(): Promise<TestCase> {
  const name = "discriminator >= 10000 is rejected";
  try {
    buildUsername("alice", 10_000);
    return fail(name, "buildUsername did not throw");
  } catch (e) {
    if (e instanceof UsernameValidationError && e.detail.kind === "discriminatorOutOfRange" && e.detail.value === 10_000) {
      return pass(name);
    }
    return fail(name, `wrong error: ${String(e)}`);
  }
}

// ─── Group B: formatting + parse ─────────────────────────────────────────────

async function testCanonicalStringZeroPads(): Promise<TestCase> {
  const name = "canonical string zero-pads discriminator to 4 digits";
  try {
    const u = buildUsername("alice", 1);
    const s = usernameCanonicalString(u);
    const ok = s === "alice#0001";
    return ok ? pass(name) : fail(name, `got ${s}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testParseRoundTrip(): Promise<TestCase> {
  const name = "parseUsername(canonicalString(u)) preserves value";
  try {
    const original = buildUsername("alice", 4567);
    const parsed = parseUsername(usernameCanonicalString(original));
    if (!parsed) return fail(name, "parseUsername returned null");
    const ok = parsed.canonicalName === original.canonicalName && parsed.discriminator === original.discriminator;
    return ok ? pass(name) : fail(name, `got ${JSON.stringify(parsed)}, want ${JSON.stringify(original)}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testParseFullHandleSucceeds(): Promise<TestCase> {
  const name = "parseUsername(\"alice#0001\") succeeds";
  try {
    const u = parseUsername("alice#0001");
    if (!u) return fail(name, "parseUsername returned null");
    const ok = u.canonicalName === "alice" && u.discriminator === 1;
    return ok ? pass(name) : fail(name, `got ${JSON.stringify(u)}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testParseBareNameReturnsNull(): Promise<TestCase> {
  const name = "parseUsername(\"alice\") with no discriminator returns null";
  const u = parseUsername("alice");
  return u === null ? pass(name) : fail(name, `got ${JSON.stringify(u)}`);
}

async function testParseShortDiscriminatorReturnsNull(): Promise<TestCase> {
  const name = "parseUsername(\"alice#1\") rejects 1-digit discriminator";
  const u = parseUsername("alice#1");
  return u === null ? pass(name) : fail(name, `got ${JSON.stringify(u)}`);
}

// ─── Group C: lookup hash ─────────────────────────────────────────────────────

async function testHashIsDeterministic(): Promise<TestCase> {
  const name = "lookup hash is deterministic";
  try {
    const u = buildUsername("alice", 1234);
    const a = await lookupHash(u);
    const b = await lookupHash(u);
    const ok = a.length === b.length && a.every((v, i) => v === b[i]);
    return ok ? pass(name) : fail(name, "two calls produced different hashes");
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testHashDiffersForDifferentNames(): Promise<TestCase> {
  const name = "lookup hash differs for different names";
  try {
    const a = await lookupHash(buildUsername("alice", 1234));
    const b = await lookupHash(buildUsername("bob", 1234));
    const same = a.length === b.length && a.every((v, i) => v === b[i]);
    return !same ? pass(name) : fail(name, "hashes collided for different names");
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testHashDiffersForDifferentDiscriminators(): Promise<TestCase> {
  const name = "lookup hash differs for different discriminators";
  try {
    const a = await lookupHash(buildUsername("alice", 1234));
    const b = await lookupHash(buildUsername("alice", 4567));
    const same = a.length === b.length && a.every((v, i) => v === b[i]);
    return !same ? pass(name) : fail(name, "hashes collided for different discriminators");
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testHashUnitSeparatorPreventsCollision(): Promise<TestCase> {
  // Without 0x1F, "foo"+"1234" and "foo1"+"234" would share the same
  // concatenated byte string. The separator must make their hashes differ.
  const name = "unit separator prevents foo+1234 vs foo1+234 collision";
  try {
    const a = await lookupHash(buildUsername("foo", 1234));
    const b = await lookupHash(buildUsername("foo1", 234));
    const same = a.length === b.length && a.every((v, i) => v === b[i]);
    return !same ? pass(name) : fail(name, "hashes collided despite unit separator");
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testHashLengthIs32Bytes(): Promise<TestCase> {
  const name = "lookup hash is exactly 32 bytes";
  try {
    const h = await lookupHash(buildUsername("alice", 1234));
    const ok = h.length === 32;
    return ok ? pass(name) : fail(name, `got ${h.length} bytes`);
  } catch (e) {
    return fail(name, String(e));
  }
}

// ─── Group D: claim codec ─────────────────────────────────────────────────────

async function testClaimSignVerifyRoundTrip(): Promise<TestCase> {
  const name = "sign + verify round-trips a claim";
  try {
    const u = buildUsername("alice", 1234);
    const kp = await generateEd25519KeyPair();
    const payload: UsernameClaimPayload = {
      schema: CURRENT_SCHEMA,
      username: u,
      identityPublicKey: kp.publicKey,
      claimedAt: 1_700_000_000n,
    };
    const claim = await signClaim(payload, kp.privateKey);
    const ok = await verifyClaim(claim);
    return ok ? pass(name) : fail(name, "verifyClaim returned false");
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testClaimEncodeDecodeRoundTrip(): Promise<TestCase> {
  const name = "encode + decode round-trips a claim";
  try {
    const u = buildUsername("alice", 1234);
    const kp = await generateEd25519KeyPair();
    const payload: UsernameClaimPayload = {
      schema: CURRENT_SCHEMA,
      username: u,
      identityPublicKey: kp.publicKey,
      claimedAt: 1_700_000_000n,
    };
    const claim = await signClaim(payload, kp.privateKey);
    const encoded = encodeClaim(claim);
    const decoded = await decodeClaim(encoded);
    // Structural equality check
    const payloadOk =
      decoded.payload.schema === claim.payload.schema &&
      decoded.payload.username.canonicalName === claim.payload.username.canonicalName &&
      decoded.payload.username.discriminator === claim.payload.username.discriminator &&
      decoded.payload.claimedAt === claim.payload.claimedAt &&
      decoded.payload.identityPublicKey.length === claim.payload.identityPublicKey.length &&
      decoded.payload.identityPublicKey.every((v, i) => v === claim.payload.identityPublicKey[i]) &&
      decoded.signature.length === claim.signature.length &&
      decoded.signature.every((v, i) => v === claim.signature[i]);
    return payloadOk ? pass(name) : fail(name, "decoded != original");
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testClaimTamperedClaimedAtFailsVerify(): Promise<TestCase> {
  const name = "tampering with claimedAt fails verify";
  try {
    const u = buildUsername("alice", 1234);
    const kp = await generateEd25519KeyPair();
    const original: UsernameClaimPayload = {
      schema: CURRENT_SCHEMA,
      username: u,
      identityPublicKey: kp.publicKey,
      claimedAt: 1_700_000_000n,
    };
    const signed = await signClaim(original, kp.privateKey);
    // Re-bind the same signature to a payload with a different claimedAt.
    const tampered = {
      payload: { ...original, claimedAt: 9_999_999_999n },
      signature: signed.signature,
    };
    const ok = !(await verifyClaim(tampered));
    return ok ? pass(name) : fail(name, "verifyClaim accepted tampered claim");
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testClaimWrongIdentityKeyFailsDecode(): Promise<TestCase> {
  const name = "decode with wrong identity key throws invalidSignature";
  try {
    const u = buildUsername("alice", 1234);
    const kp = await generateEd25519KeyPair();
    const otherKp = await generateEd25519KeyPair();
    const payload: UsernameClaimPayload = {
      schema: CURRENT_SCHEMA,
      username: u,
      identityPublicKey: kp.publicKey,
      claimedAt: 1_700_000_000n,
    };
    const signed = await signClaim(payload, kp.privateKey);
    // Swap the identity public key inside the payload — signature now
    // binds to a payload that claims a different identity.
    const swapped = {
      payload: { ...payload, identityPublicKey: otherKp.publicKey },
      signature: signed.signature,
    };
    const encoded = encodeClaim(swapped);
    try {
      await decodeClaim(encoded);
      return fail(name, "decodeClaim did not throw");
    } catch (e) {
      if (e instanceof UsernameClaimError && e.detail.kind === "invalidSignature") {
        return pass(name);
      }
      return fail(name, `wrong error: ${String(e)}`);
    }
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testClaimIsClaimDistinguishesFromSiblings(): Promise<TestCase> {
  const name = "isClaim distinguishes from chat/envelope/receipt/tombstone/reply/pin/draft/skdm/skmsg/role/gmember/voice/caption/voipush/call:link/call:revoke/sticker";
  try {
    const u = buildUsername("alice", 1);
    const kp = await generateEd25519KeyPair();
    const payload: UsernameClaimPayload = {
      schema: CURRENT_SCHEMA,
      username: u,
      identityPublicKey: kp.publicKey,
      claimedAt: 1n,
    };
    const signed = await signClaim(payload, kp.privateKey);
    const encoded = encodeClaim(signed);

    const positive    = isClaim(encoded);
    const negChat     = isClaim("hello world");
    const negEnvelope = isClaim("kaether:e2ee:v1:foo");
    const negReceipt  = isClaim("kaether:receipt:v1:abc");
    const negTomb     = isClaim("kaether:tombstone:v1:abc");
    const negReply    = isClaim("kaether:reply:v1:abc");
    const negPin      = isClaim("kaether:pin:v1:abc");
    const negDraft    = isClaim("kaether:draft:v1:abc");
    const negSkdm     = isClaim("kaether:skdm:v1:abc");
    const negSkmsg    = isClaim("kaether:skmsg:v1:abc");
    const negRole     = isClaim("kaether:role:v1:abc");
    const negGmember  = isClaim("kaether:gmember:v1:abc");
    const negVoice    = isClaim("kaether:voice:v1:abc");
    const negCaption  = isClaim("kaether:caption:v1:abc");
    const negVoipush  = isClaim("kaether:voipush:v1:abc");
    const negCallLink = isClaim("kaether:call:link:v1:abc");
    const negCallRev  = isClaim("kaether:call:revoke:v1:abc");
    const negSticker  = isClaim("kaether:sticker:pack:v1:abc");

    const ok = positive
      && !negChat && !negEnvelope && !negReceipt && !negTomb
      && !negReply && !negPin && !negDraft && !negSkdm && !negSkmsg
      && !negRole && !negGmember && !negVoice && !negCaption
      && !negVoipush && !negCallLink && !negCallRev && !negSticker;
    return ok ? pass(name) : fail(
      name,
      `pos=${positive} chat=${negChat} env=${negEnvelope} rcpt=${negReceipt}`
    );
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testClaimUnsupportedSchemaRejected(): Promise<TestCase> {
  const name = "unsupported claim schema is rejected";
  try {
    const u = buildUsername("alice", 1);
    const kp = await generateEd25519KeyPair();
    // Build a schema-99 payload and sign it so the signature is structurally
    // valid — decode must still reject for schema.
    const payload: UsernameClaimPayload = {
      schema: 99,
      username: u,
      identityPublicKey: kp.publicKey,
      claimedAt: 1n,
    };
    // We need to sign the canonical bytes for schema 99 manually using
    // raw SubtleCrypto, bypassing signClaim's schema check. Chrome's W3C
    // Ed25519 refuses `importKey("raw", ...)` for private keys — the 32-byte
    // seed must be wrapped in PKCS#8 first. Same 16-byte ASN.1 header as
    // `sender-keys/SenderKey.ts::ED25519_PKCS8_PREFIX`.
    const canonical = claimCanonicalBytes(payload);
    const PKCS8_PREFIX = new Uint8Array([
      0x30, 0x2e, 0x02, 0x01, 0x00, 0x30, 0x05, 0x06,
      0x03, 0x2b, 0x65, 0x70, 0x04, 0x22, 0x04, 0x20,
    ]);
    const pkcs8 = new Uint8Array(PKCS8_PREFIX.length + kp.privateKey.length);
    pkcs8.set(PKCS8_PREFIX, 0);
    pkcs8.set(kp.privateKey, PKCS8_PREFIX.length);
    const privKey = await crypto.subtle.importKey(
      "pkcs8",
      pkcs8,
      { name: "Ed25519" },
      false,
      ["sign"],
    );
    const sigBuf = await crypto.subtle.sign({ name: "Ed25519" }, privKey, canonical);
    const claim = { payload, signature: new Uint8Array(sigBuf) };
    const encoded = encodeClaim(claim);

    try {
      await decodeClaim(encoded);
      return fail(name, "decodeClaim did not throw");
    } catch (e) {
      if (e instanceof UsernameClaimError && e.detail.kind === "unsupportedSchema" && (e.detail as { kind: "unsupportedSchema"; schema: number }).schema === 99) {
        return pass(name);
      }
      return fail(name, `wrong error: ${String(e)}`);
    }
  } catch (e) {
    return fail(name, String(e));
  }
}

// ─── Group E: discoverability policy ─────────────────────────────────────────

async function testFuzzyNilIsFalse(): Promise<TestCase> {
  const name = "nil settings → not in fuzzy index";
  const ok = !canAppearInFuzzySearch(null);
  return ok ? pass(name) : fail(name, "policy allowed null settings in fuzzy index");
}

async function testFuzzyUndefinedIsFalse(): Promise<TestCase> {
  const name = "undefined settings → not in fuzzy index";
  const ok = !canAppearInFuzzySearch(undefined);
  return ok ? pass(name) : fail(name, "policy allowed undefined settings in fuzzy index");
}

async function testFuzzyDirectLookupOnlyIsFalse(): Promise<TestCase> {
  const name = ".directLookupOnly → not in fuzzy index";
  try {
    const u = buildUsername("alice", 1);
    const s = { username: u, mode: "directLookupOnly" as const, updatedAt: 1 };
    const ok = !canAppearInFuzzySearch(s);
    return ok ? pass(name) : fail(name, "policy allowed directLookupOnly in fuzzy index");
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testFuzzyDiscoverableIsTrue(): Promise<TestCase> {
  const name = ".discoverable → in fuzzy index";
  try {
    const u = buildUsername("alice", 1);
    const s = { username: u, mode: "discoverable" as const, updatedAt: 1 };
    const ok = canAppearInFuzzySearch(s);
    return ok ? pass(name) : fail(name, "policy refused discoverable in fuzzy index");
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testDefaultForIsDirectLookupOnly(): Promise<TestCase> {
  const name = "defaultFor returns .directLookupOnly (privacy-preserving default)";
  try {
    const u = buildUsername("alice", 1);
    const s = defaultDiscoverabilitySettings(u, 42);
    const ok =
      s.mode === "directLookupOnly" &&
      s.username.canonicalName === u.canonicalName &&
      s.username.discriminator === u.discriminator &&
      s.updatedAt === 42;
    return ok ? pass(name) : fail(name, `got ${JSON.stringify(s)}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

// ─── register suite ───────────────────────────────────────────────────────────

SelfTestRunner.register({
  name: "Username system (claims + discoverability)",
  isVerified: true,

  run: async () => {
    const cases = await Promise.all([
      // Group A: canonicalisation
      testValidName(),
      testUppercaseLowercased(),
      testWhitespaceTrimmed(),
      testNonNFCRejected(),
      testCyrillicHomographRejected(),
      testNameTooShortRejected(),
      testNameTooLongRejected(),
      testLeadingDigitRejected(),
      testDiscriminatorOutOfRangeRejected(),

      // Group B: formatting + parse
      testCanonicalStringZeroPads(),
      testParseRoundTrip(),
      testParseFullHandleSucceeds(),
      testParseBareNameReturnsNull(),
      testParseShortDiscriminatorReturnsNull(),

      // Group C: lookup hash
      testHashIsDeterministic(),
      testHashDiffersForDifferentNames(),
      testHashDiffersForDifferentDiscriminators(),
      testHashUnitSeparatorPreventsCollision(),
      testHashLengthIs32Bytes(),

      // Group D: claim codec
      testClaimSignVerifyRoundTrip(),
      testClaimEncodeDecodeRoundTrip(),
      testClaimTamperedClaimedAtFailsVerify(),
      testClaimWrongIdentityKeyFailsDecode(),
      testClaimIsClaimDistinguishesFromSiblings(),
      testClaimUnsupportedSchemaRejected(),

      // Group E: discoverability policy
      testFuzzyNilIsFalse(),
      testFuzzyUndefinedIsFalse(),
      testFuzzyDirectLookupOnlyIsFalse(),
      testFuzzyDiscoverableIsTrue(),
      testDefaultForIsDirectLookupOnly(),
    ]);
    return buildSuiteResult(cases);
  },
});
